import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Check, X, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, uid } from '../lib/model';

const fmt = (v) => `${(+v).toFixed(2).replace('.', ',')} m`;

function arrow(cx, a, b, lw) {
  const ang = Math.atan2(b.y - a.y, b.x - a.x), L = lw * 6;
  cx.setLineDash([]); cx.fillStyle = '#dc2626';
  cx.beginPath(); cx.moveTo(b.x, b.y);
  cx.lineTo(b.x - L * Math.cos(ang - 0.4), b.y - L * Math.sin(ang - 0.4));
  cx.lineTo(b.x - L * Math.cos(ang + 0.4), b.y - L * Math.sin(ang + 0.4));
  cx.closePath(); cx.fill();
}

function dot(cx, p, label, lw, color) {
  cx.setLineDash([]); cx.lineWidth = lw; cx.strokeStyle = '#fff'; cx.fillStyle = color;
  cx.beginPath(); cx.arc(p.x, p.y, lw * 3, 0, Math.PI * 2); cx.fill(); cx.stroke();
  cx.beginPath(); cx.moveTo(p.x - lw * 6, p.y); cx.lineTo(p.x + lw * 6, p.y); cx.moveTo(p.x, p.y - lw * 6); cx.lineTo(p.x, p.y + lw * 6); cx.stroke();
  box(cx, label, p.x + lw * 5, p.y - lw * 7, lw, color, '#fff', 'left');
}

function box(cx, txt, x, y, lw, bg, fg, align = 'center') {
  cx.font = `700 ${Math.round(lw * 5.5)}px sans-serif`;
  const w = cx.measureText(txt).width + lw * 4, h = lw * 8, x0 = align === 'center' ? x - w / 2 : x;
  cx.fillStyle = bg; cx.fillRect(x0, y - h / 2, w, h);
  cx.strokeStyle = '#0f172a'; cx.lineWidth = Math.max(1, lw / 2); cx.setLineDash([]); cx.strokeRect(x0, y - h / 2, w, h);
  cx.fillStyle = fg; cx.textAlign = 'center'; cx.textBaseline = 'middle'; cx.fillText(txt, x0 + w / 2, y);
}

// burn crosshair marks + dashed dimension arrows into the photo
function compose(img, marks, lines) {
  const c = document.createElement('canvas');
  c.width = img.w; c.height = img.h;
  const cx = c.getContext('2d');
  return new Promise((res) => {
    const im = new Image();
    im.onload = () => {
      cx.drawImage(im, 0, 0, img.w, img.h);
      const lw = Math.max(3, Math.round(Math.max(img.w, img.h) / 320));
      const P = marks.map((m) => ({ ...m, x: m.x * img.w, y: m.y * img.h }));
      lines.forEach(([i, j, label]) => {
        const a = P[i], b = P[j];
        cx.lineCap = 'round';
        cx.setLineDash([]); cx.strokeStyle = '#facc15'; cx.lineWidth = lw * 2.2; cx.beginPath(); cx.moveTo(a.x, a.y); cx.lineTo(b.x, b.y); cx.stroke();
        cx.setLineDash([lw * 4, lw * 3]); cx.strokeStyle = '#dc2626'; cx.lineWidth = lw; cx.beginPath(); cx.moveTo(a.x, a.y); cx.lineTo(b.x, b.y); cx.stroke();
        arrow(cx, a, b, lw);
        box(cx, label, (a.x + b.x) / 2, (a.y + b.y) / 2, lw, '#facc15', '#0f172a');
      });
      P.forEach((p) => dot(cx, p, p.label, lw, p.target ? '#1d6fe8' : '#16a34a'));
      res(c.toDataURL('image/jpeg', 0.85));
    };
    im.src = img.data;
  });
}

function Marker({ t, img, steps, onDone, onClose }) {
  const [pos, setPos] = useState({ x: 0.5, y: 0.5 });
  const [marks, setMarks] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const st = steps[marks.length];
  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    setPos({ x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) });
  };
  const confirm = async () => {
    const next = [...marks, { ...pos, label: st.label, target: st.target }];
    if (next.length < steps.length) return setMarks(next);
    setBusy(true);
    const ti = next.length - 1;
    const data = await compose(img, next, steps.slice(0, -1).map((s, i) => [i, ti, `${s.label} ─ ${s.dist} ─► ${steps[ti].label}`]));
    onDone({ id: uid('ph'), kind: 'photo', svy: true, caption: steps.slice(0, -1).map((s) => `${s.label} ─ ${s.dist} ─► ${steps[ti].label}`).join(' · '), data, w: img.w, h: img.h });
  };
  return (
    <div className="svp-ov" data-testid="survey-photo-marker">
      <div className="svp-head">
        <b data-testid="survey-photo-step">📷 {marks.length + 1}/{steps.length}: {st.hint}</b>
        <button className="icon-btn" data-testid="survey-photo-close-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="svp-stage">
        <div className="svp-img-wrap" ref={ref} data-testid="survey-photo-stage" onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); move(e); }} onPointerMove={(e) => e.buttons && move(e)}>
          <img src={img.data} alt="" draggable={false} />
          {marks.map((m, i) => <span key={i} className={`svp-mark ${m.target ? 'tg' : ''}`} style={{ left: `${m.x * 100}%`, top: `${m.y * 100}%` }}>{m.label}</span>)}
          <span className="svp-cross" data-testid="survey-photo-crosshair" style={{ left: `${pos.x * 100}%`, top: `${pos.y * 100}%` }} />
        </div>
      </div>
      <div className="svp-foot">
        {marks.length > 0 && <button className="btn btn-outline btn-sm" data-testid="survey-photo-undo-btn" onClick={() => setMarks(marks.slice(0, -1))}><Undo2 size={15} /> {t.svyBack}</button>}
        <button className="btn btn-green" disabled={busy} data-testid="survey-photo-confirm-btn" onClick={confirm}><Check size={16} /> {st.btn}</button>
      </div>
    </div>
  );
}

// photo button: take/choose a photo, mark fixed point(s) and target with a crosshair
export function SurveyPhotoBtn({ t, mode, dists, refLabel, target, onAdd, testid }) {
  const inRef = useRef(null);
  const [img, setImg] = useState(null);
  const pts = mode === 'ab' ? ['a', 'b'] : [mode];
  const open = () => {
    if (pts.some((k) => !(+String(dists[k]).replace(',', '.') > 0))) return toast.error(t.svyNeedDist);
    inRef.current.click();
  };
  const steps = [
    ...pts.map((k) => { const l = `${k === 'a' ? 'P1' : 'P2'} (${refLabel})`; return { label: l, dist: fmt(String(dists[k]).replace(',', '.')), hint: t.svpAimFix.replace('{p}', l), btn: t.svpConfirmFix.replace('{p}', k === 'a' ? 'P1' : 'P2') }; }),
    { label: target, target: true, hint: t.svpAimTarget.replace('{o}', target), btn: t.svpConfirmTarget },
  ];
  const lbl = mode === 'a' ? t.svpBtnA : mode === 'b' ? t.svpBtnB : t.svpBtnAB;
  return (
    <>
      <button className="btn btn-outline btn-sm" data-testid={testid} onClick={open} title={t.svpTip.replace('{p}', mode === 'b' ? 'P2' : 'P1').replace('{o}', target)}><Camera size={14} /> {lbl}</button>
      <input ref={inRef} type="file" accept="image/*" capture="environment" hidden data-testid={`${testid}-input`} onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setImg(await compressImage(f, 1600, 0.85)); }} />
      {img && createPortal(<Marker t={t} img={img} steps={steps} onClose={() => setImg(null)} onDone={(p) => { onAdd(p); setImg(null); toast.success(t.svpSaved); }} />, document.body)}
    </>
  );
}
